// ============================================================
// Dokumente-Übersicht — alle gespeicherten Dateien an einem Ort:
// Akte-Anhänge (Belege/Dokumente/Fotos) + Protokoll-Anhänge, firmen-
// übergreifend. Neu: Sonderzuordnung „Allgemein"/„Privat" (ohne Firma),
// Datei hochladen, Zuordnung ändern, löschen. Filter + Suche.
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMandantStore } from '../../store/mandantStore';
import { useFocusStore } from '../../store/focusStore';
import { loadAkte, deleteAkteEintrag, addDokument, setAkteMandant } from '../../services/akteService';
import { loadProtokolle, removeAnhang } from '../../services/protokollService';
import { prepareAnhang } from '../../services/captureService';
import { SONDER_KATEGORIEN, sonderKatName, KAT_ALLGEMEIN } from '../../lib/dokKategorien';

interface DokDoc {
  id:        string;
  name:      string;
  mime:      string;
  b64:       string;
  mandantId?: string;
  firma:     string;
  datum:     string;
  quelle:    'akte' | 'protokoll';
  refId:     string;      // Akte-Eintrag-ID bzw. Protokoll-ID
  anhangId?: string;      // bei Protokoll-Anhang
  typLabel:  string;
}

function fmt(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}.${m}.${y}` : iso;
}
function dataUrl(d: { mime: string; b64: string }): string { return `data:${d.mime};base64,${d.b64}`; }

export function DokumenteModul(): React.JSX.Element {
  const mandanten = useMandantStore(s => s.mandanten);
  const requestFocus = useFocusStore(s => s.request);
  const firmaOf = useCallback(
    (id?: string) => (id !== undefined && id !== ''
      ? (sonderKatName(id) ?? mandanten.find(m => m.id === id)?.name ?? id) : '—'),
    [mandanten],
  );

  const [docs, setDocs] = useState<DokDoc[]>([]);
  const [firma, setFirma] = useState('');
  const [typ, setTyp] = useState<'ALLE' | 'BILD' | 'PDF'>('ALLE');
  const [suche, setSuche] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);

  // Zuordnungs-Optionen: Sonderkategorien zuerst, dann Firmen.
  const zuordnungen = useMemo(
    () => [...SONDER_KATEGORIEN, ...mandanten.map(m => ({ id: m.id, name: m.name }))],
    [mandanten],
  );

  const reload = useCallback(async () => {
    const [akte, protokolle] = await Promise.all([loadAkte(), loadProtokolle()]);
    const list: DokDoc[] = [];
    for (const e of akte) {
      if (e.anhang_b64 === undefined || e.anhang_b64 === '' || e.anhang_mime === undefined) continue;
      list.push({
        id: `akte-${e.id}`, name: e.titel || '(ohne Titel)', mime: e.anhang_mime, b64: e.anhang_b64,
        firma: firmaOf(e.mandant_id), datum: e.datum, quelle: 'akte', refId: e.id, typLabel: e.typ,
        ...(e.mandant_id !== undefined ? { mandantId: e.mandant_id } : {}),
      });
    }
    for (const p of protokolle) {
      for (const a of p.anhaenge) {
        list.push({
          id: `prot-${p.id}-${a.id}`, name: a.name, mime: a.mime, b64: a.b64,
          firma: firmaOf(p.mandant_id), datum: p.datum, quelle: 'protokoll', refId: p.id, anhangId: a.id,
          typLabel: 'Protokoll',
          ...(p.mandant_id !== undefined ? { mandantId: p.mandant_id } : {}),
        });
      }
    }
    list.sort((x, y) => y.datum.localeCompare(x.datum));
    setDocs(list);
  }, [firmaOf]);

  useEffect(() => { void reload(); }, [reload]);

  const gefiltert = useMemo(() => docs.filter(d => {
    if (typ === 'BILD' && !d.mime.startsWith('image/')) return false;
    if (typ === 'PDF' && d.mime !== 'application/pdf') return false;
    if (firma !== '' && d.mandantId !== firma) return false;
    if (suche.trim() !== '') {
      const q = suche.toLowerCase();
      if (!`${d.name} ${d.firma} ${d.typLabel}`.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [docs, typ, firma, suche]);

  async function reassign(d: DokDoc, mandantId: string): Promise<void> {
    await setAkteMandant(d.refId, mandantId);
    await reload();
  }
  async function loeschen(d: DokDoc): Promise<void> {
    if (!window.confirm(`„${d.name}" löschen?`)) return;
    if (d.quelle === 'akte') await deleteAkteEintrag(d.refId);
    else if (d.anhangId !== undefined) await removeAnhang(d.refId, d.anhangId);
    await reload();
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">📁 Dokumente</h1>
          <p className="text-sm text-gray-500">Alle Dateien firmenübergreifend — aus Akte, Protokollen und eigenen Uploads.</p>
        </div>
        <button onClick={() => { setUploadOpen(true); }} className="btn-primary text-sm">＋ Dokument</button>
      </div>

      {/* Filterleiste */}
      <div className="card flex flex-wrap items-center gap-3">
        <input value={suche} onChange={e => { setSuche(e.target.value); }}
          placeholder="🔍 Suchen (Name, Firma…)" className="input-field flex-1 min-w-[12rem]" />
        <select value={firma} onChange={e => { setFirma(e.target.value); }} className="input-field w-auto">
          <option value="">Alle Zuordnungen</option>
          {SONDER_KATEGORIEN.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
          <option disabled>──────────</option>
          {mandanten.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <div className="flex gap-1.5">
          {(['ALLE', 'BILD', 'PDF'] as const).map(t => (
            <button key={t} onClick={() => { setTyp(t); }}
              className={`text-xs px-2.5 py-1.5 rounded-full font-medium transition-colors ${
                typ === t ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200'}`}>
              {t === 'ALLE' ? 'Alle' : t === 'BILD' ? '🖼 Bilder' : '📄 PDF'}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-400">{gefiltert.length} Dokument(e)</p>

      {gefiltert.length === 0 ? (
        <div className="card text-center py-12 text-gray-400">
          <p className="text-4xl mb-2">📁</p>
          <p className="text-sm">Keine Dokumente. Lade eins hoch („＋ Dokument"), hänge welche an ein Protokoll an oder erfasse Belege in der Akte (📷).</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {gefiltert.map(d => (
            <div key={d.id} className="card p-3 flex flex-col gap-2">
              <a href={dataUrl(d)} {...(d.mime === 'application/pdf' ? { download: d.name } : { target: '_blank', rel: 'noopener' })}
                className="block">
                {d.mime.startsWith('image/') ? (
                  <img src={dataUrl(d)} alt={d.name}
                    className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-700" />
                ) : (
                  <div className="w-full h-32 flex items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-800 text-4xl">📄</div>
                )}
              </a>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={d.name}>{d.name}</p>
                <p className="text-xs text-gray-400 truncate">
                  {d.firma} · {fmt(d.datum)} · {d.quelle === 'protokoll' ? '📝 Protokoll' : `🗂 ${d.typLabel}`}
                </p>
              </div>

              {/* Zuordnung (nur für Akte-Dokumente änderbar) */}
              {d.quelle === 'akte' ? (
                <select value={d.mandantId ?? ''} onChange={e => { void reassign(d, e.target.value); }}
                  className="input-field text-xs py-1" title="Zuordnung ändern">
                  {d.mandantId === undefined && <option value="">— ohne —</option>}
                  {zuordnungen.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              ) : (
                <p className="text-[11px] text-gray-400">Zuordnung über das Protokoll</p>
              )}

              <div className="flex items-center gap-3 text-xs">
                <a href={dataUrl(d)} {...(d.mime === 'application/pdf' ? { download: d.name } : { target: '_blank', rel: 'noopener' })}
                  className="text-blue-600 hover:underline">Öffnen</a>
                <button onClick={() => { requestFocus(d.quelle === 'akte' ? 'akte' : 'protokolle', d.refId); }}
                  className="text-gray-500 hover:text-blue-600">↗ Quelle</button>
                <button onClick={() => { void loeschen(d); }}
                  className="ml-auto text-red-500 hover:text-red-700">🗑 löschen</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {uploadOpen && (
        <UploadModal zuordnungen={zuordnungen} vorauswahl={firma !== '' ? firma : KAT_ALLGEMEIN}
          onClose={() => { setUploadOpen(false); }} onSaved={() => { setUploadOpen(false); void reload(); }} />
      )}
    </div>
  );
}

// ── Upload-Dialog ──────────────────────────────────────────────────────────

function UploadModal({ zuordnungen, vorauswahl, onClose, onSaved }: {
  zuordnungen: { id: string; name: string }[];
  vorauswahl: string;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const [titel, setTitel] = useState('');
  const [zuordnung, setZuordnung] = useState(vorauswahl);
  const [datei, setDatei] = useState<{ b64: string; mime: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');

  async function waehle(file: File): Promise<void> {
    try {
      const { b64, mime } = await prepareAnhang(file);
      setDatei({ b64, mime, name: file.name });
      if (titel.trim() === '') setTitel(file.name.replace(/\.[^.]+$/, ''));
      setFehler('');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  async function speichern(): Promise<void> {
    if (datei === null) return;
    setBusy(true);
    await addDokument({ mandant_id: zuordnung, titel, b64: datei.b64, mime: datei.mime });
    onSaved();
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl p-5 space-y-4"
        onClick={e => { e.stopPropagation(); }}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">📁 Dokument hochladen</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <label className="block">
          <span className="text-xs text-gray-500 dark:text-gray-400">Datei (Bild oder PDF ≤ 4 MB)</span>
          <input type="file" accept="image/*,application/pdf" className="mt-1 w-full text-sm"
            onChange={e => { const f = e.target.files?.[0]; if (f) void waehle(f); }} />
          {datei !== null && (
            <span className="text-xs text-green-600 dark:text-green-400">✓ {datei.name}</span>
          )}
        </label>

        <label className="block">
          <span className="text-xs text-gray-500 dark:text-gray-400">Titel</span>
          <input value={titel} onChange={e => { setTitel(e.target.value); }}
            className="mt-1 input-field text-sm" placeholder="Bezeichnung des Dokuments" />
        </label>

        <label className="block">
          <span className="text-xs text-gray-500 dark:text-gray-400">Zuordnung</span>
          <select value={zuordnung} onChange={e => { setZuordnung(e.target.value); }} className="mt-1 input-field text-sm">
            {zuordnungen.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
        </label>

        {fehler !== '' && <p className="text-xs text-red-600 dark:text-red-400">{fehler}</p>}

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-300">Abbrechen</button>
          <button onClick={() => void speichern()} disabled={datei === null || busy}
            className="flex-1 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-3 py-2 text-sm font-medium text-white">
            {busy ? 'Speichern…' : 'Hochladen'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
